/*
  opening act. — stricter storefront search.

  Shopify's default /search matches a word ANYWHERE: title, vendor, type, tags,
  SKU, but also the full product description, plus typo tolerance. With our
  long styling descriptions that pulled in lots of unrelated products
  ("dress" -> button-up shirts, "jacket" -> bucket hats, "prada" -> "Frida",
  "boots" -> bootcut jeans).

  So every search the shop sends goes through here and is rewritten into
  Shopify's field syntax. Each word must match title, vendor, product type,
  tag or SKU:
      leather jacket  ->  (title:leather OR vendor:leather OR ...) (title:jacket OR ...)

  Exception: "shoes"/"shoe"/"schuhe" also match product type Footwear (no
  title says "shoes"; the plain search found them via the product category).

  Colours are deliberately NOT special-cased: they mostly live in the
  descriptions, but a plain "black" matched ~905 results for 750 products
  (fuzzy/semantic matching), and Shopify's body: field filter doesn't
  narrow anything. So a colour only matches via title or tag — tag
  products with their colour to make them findable by colour.

  Field terms don't get prefix matching, so a half-typed word ("prad") finds
  nothing strictly. Hence the fallback levels, tried in order until one has
  results:
      0  every word strict
      1  every word strict except the last, which stays plain (+ prefix)
      2  plain Shopify search (old behaviour)
  predictive-search.js and the search page's live update walk the levels via
  fetch; a submitted search that lands on 0 results is walked forward by a
  tiny redirect in main-search.liquid. Which level is "good enough" is judged
  by the total INCLUDING sold pieces (see "Sold pieces last" below).

  The visible input always shows the user's own words: OASearch.display(q)
  (and the Liquid twin snippets/oa-search-terms.liquid) turn a rewritten
  query back into them.
*/
(function () {
  var EXTRA = {
    shoes: ['product_type:footwear'],
    shoe: ['product_type:footwear'],
    schuhe: ['product_type:footwear'],
  };
  var FIELDS = ['title', 'vendor', 'product_type', 'tag', 'variants.sku'];

  // Split raw input into safe, lower-case words (no syntax characters, so a
  // typed "OR", "-" or "title:" can't turn into a query operator).
  function words(raw) {
    return String(raw || '')
      .toLowerCase()
      .replace(/[()"':\\*]/g, ' ')
      .split(/\s+/)
      .map(function (w) {
        return w.replace(/^-+/, '');
      })
      .filter(Boolean);
  }

  function strict(w) {
    var parts = FIELDS.map(function (f) {
      return f + ':' + w;
    }).concat(EXTRA[w] || []);
    return '(' + parts.join(' OR ') + ')';
  }

  // Furniture area (layout/moebel adds body.oa-mb): every search is limited
  // to the furniture pieces by an extra "tag:Furniture" term, and lands on
  // the furniture search page (?view=moebel).
  var SCOPE = 'tag:Furniture';
  function scoped() {
    return !!(document.body && document.body.classList.contains('oa-mb'));
  }

  function build(raw, level) {
    var ws = words(raw);
    if (!ws.length) return '';
    var q;
    if (level >= 2) q = ws.join(' ');
    else
      q = ws
        .map(function (w, i) {
          return level === 1 && i === ws.length - 1 ? w : strict(w);
        })
        .join(' ');
    return scoped() ? q + ' ' + SCOPE : q;
  }

  // Distinct queries to try, strictest first.
  function levels(raw) {
    var out = [];
    [0, 1, 2].forEach(function (l) {
      var q = build(raw, l);
      if (out.indexOf(q) === -1) out.push(q);
    });
    return out;
  }

  // Rewritten query -> the words the user typed.
  function display(q) {
    q = String(q || '').replace(/\s*\btag:Furniture\b/gi, '').trim();
    if (q.indexOf('(title:') === -1) return q;
    var out = [];
    q.split(/\s+/).forEach(function (t) {
      if (!t || t === 'OR') return;
      if (t.indexOf('(title:') === 0) out.push(t.slice(7));
      else if (t.indexOf(':') === -1) out.push(t.replace(/[()]/g, ''));
    });
    return out.join(' ');
  }

  // The next, looser query after q returned nothing (null = none left).
  function next(q) {
    var list = levels(display(q));
    var i = list.indexOf(q);
    return i !== -1 && i < list.length - 1 ? list[i + 1] : null;
  }

  // Regex that marks every typed word in a title (longest first, so
  // "leather jacket" highlights both words, not only the exact phrase).
  function highlightRegex(raw) {
    var ws = words(display(raw))
      .filter(function (w) {
        return w.length > 1;
      })
      .sort(function (a, b) {
        return b.length - a.length;
      })
      .map(function (w) {
        return w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      });
    return ws.length ? new RegExp(ws.join('|'), 'ig') : null;
  }

  // ---- Sold pieces last -------------------------------------------------
  // Shopify's options[unavailable_products]=last does not reliably put sold
  // products at the end here, but =hide reliably drops them. So searches run
  // in two phases: first the available pieces (=hide), then — via SHOW MORE —
  // the sold ones, taken from the =show results (cards/list items carry
  // data-oa-available so the available ones aren't added twice).
  var UNAVAILABLE = 'options[unavailable_products]';

  // Total result count INCLUDING sold pieces for the given search params
  // (URLSearchParams or string; filters/sort are kept). Uses the small
  // predictive-search section, which prints <span data-oa-total>.
  var totalCache = {};
  function total(params) {
    var p = new URLSearchParams(params);
    p.set(UNAVAILABLE, 'show');
    p.set('type', 'product');
    p.delete('page');
    p.delete('section_id');
    p.set('section_id', 'predictive-search');
    var key = p.toString();
    if (totalCache[key]) return totalCache[key];
    var base = (window.routes && window.routes.search_url) || '/search';
    totalCache[key] = fetch(base + '?' + key)
      .then(function (r) {
        return r.text();
      })
      .then(function (t) {
        var m = t.match(/data-oa-total="(\d+)"/);
        return m ? parseInt(m[1], 10) : 0;
      })
      .catch(function () {
        delete totalCache[key];
        return 0;
      });
    return totalCache[key];
  }

  window.OASearch = {
    words: words,
    build: build,
    levels: levels,
    display: display,
    next: next,
    highlightRegex: highlightRegex,
    total: total,
    UNAVAILABLE: UNAVAILABLE,
  };

  // Every search form (header modal, search page, anywhere else) submits the
  // strict query instead of the raw text.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (event.defaultPrevented || !form || form.tagName !== 'FORM') return;
    var input = form.querySelector('input[type="search"][name="q"]');
    if (!input) return;
    var raw = input.value.trim();
    if (!raw) return;
    event.preventDefault();
    var params = new URLSearchParams(new FormData(form));
    params.set('q', build(raw, 0));
    params.set(UNAVAILABLE, 'hide'); // available first, sold via SHOW MORE
    if (scoped()) params.set('view', 'moebel');
    var action = form.getAttribute('action') || window.location.pathname;
    window.location.href = action + (action.indexOf('?') === -1 ? '?' : '&') + params.toString();
  });
})();
